import { useState } from "react";
import styles from "./HealthChat.module.css";
import translations from "./translations.json";

import { BotIcon, SosIcon, CloseIcon } from "./icons";

import WelcomeScreen from "./screens/WelcomeScreen";
import ConsentScreen from "./screens/ConsentScreen";
import PersonalizePersonaScreen from "./screens/PersonalizePersonaScreen";
import ChatHomeScreen from "./screens/ChatHomeScreen";
import SymptomPickerScreen from "./screens/SymptomPickerScreen";
import SymptomChatScreen from "./screens/SymptomChatScreen";
import TriageScreen from "./screens/TriageScreen";
import TriageResultsScreen from "./screens/TriageResultsScreen";
import RecommendedScreen from "./screens/RecommendedScreen";
import ConsultTypeScreen from "./screens/ConsultTypeScreen";
import SelectCareTypeScreen from "./screens/SelectCareTypeScreen";
import SelectVisitModelScreen from "./screens/SelectVisitModelScreen";
import SelectDoctorScreen from "./screens/SelectDoctorScreen";
import SummaryScreen from "./screens/SummaryScreen";
import WhatNextScreen from "./screens/WhatNextScreen";
import HealthProgramsScreen from "./screens/HealthProgramsScreen";
import PatientInfoScreen from "./screens/PatientInfoScreen";
import ExistingPatientScreen from "./screens/ExistingPatientScreen";
import PatientDetailsScreen from "./screens/PatientDetailsScreen";
import BookAppointmentScreen from "./screens/BookAppointmentScreen";
import ConfirmAppointmentScreen from "./screens/ConfirmAppointmentScreen";
import RatingScreen from "./screens/RatingScreen";
import ChatScreen from "./screens/ChatScreen";
import LifeSatgeScreen from "./screens/LifeStageScreen";
import HealthAssessment from "./screens/HealthAssessment";
import ReasonSelectionScreen from "./screens/ReasonSelectionScreen";
import BookingPatientDetailsScreen from "./screens/BookingPatientDetailsScreen";
import PaymentScreen from "./screens/BookingPayment";
import BookingSuccessScreen from "./screens/BookingSuccessScreen";
import ProgramDetailScreen from "./screens/ProgramDetailScreen";
import CheckoutScreen from "./screens/CheckoutScreen";
import SuccessScreen from "./screens/SuccessScreen";
import ChatCareNavigatorWelcomeScreen from "./screens/ChatCareNavigatorWelcomeScreen";

export const SCREEN = {
  CARENAVIGATOR:"CareNavigator",
  WELCOME: "welcome",
  CONSENT: "consent",
  PATIENT_PERSONA: "patient_persona",
  CHAT_HOME: "chat_home",
  SYMPTOM_PICKER: "symptom_picker",
  SYMPTOM_CHAT: "symptom_chat",
  TRIAGE: "triage",
  TRIAGE_RESULTS: "triage_results",
  RECOMMENDED: "recommended",
  // 4 new booking screens
  CONSULT_TYPE: "consult_type",
  SELECT_CARE_TYPE: "select_care_type",
  SELECT_VISIT_MODEL: "select_visit_model",
  SELECT_DOCTOR: "select_doctor",
  // existing
  BOOK_APPOINTMENT: "book_appointment",
  CONFIRM_APPOINTMENT: "confirm_appointment",
  SUMMARY: "summary",
  WHAT_NEXT: "what_next",
  HEALTH_PROGRAMS: "health_programs",
  PATIENT_INFO: "patient_info",
  EXISTING_PATIENT: "existing_patient",
  PATIENT_DETAILS: "patient_details",
  RATING: "rating",
  CHAT: "ask",
  LIFESTAGE: "LifeSatge",
  HEALTHASSESSMENT: "HealthAssessment",
  REASONSELECTION: "ReasonSelectionScreen",

  BOOKINGPATIENTDETAILS: "BookingPatientDetailsScreen",
  PAYMENT: "PaymentScreen",
  BOOKINGSUCCESSSCREEN: "BookingSuccessScreen",
  PROGRAMS_DETAILS: "ProgramDetailScreen",
  CHECKOUTSCREEN: "CheckoutScreen",
  SUCCESSSCREEN: "SuccessScreen",
};

const SOS_SCREENS = new Set([
  SCREEN.SYMPTOM_PICKER,
  SCREEN.SYMPTOM_CHAT,
  SCREEN.TRIAGE,
  SCREEN.TRIAGE_RESULTS,
  SCREEN.RECOMMENDED,
  SCREEN.SUMMARY,
]);

const NO_HEADER_SCREENS = new Set([SCREEN.WELCOME]);

function Header({ t, onClose, showSos }) {
  return (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        {/* <img src="/images/boticon.svg" alt="bot" width={32} height={32} /> */}
        <img src="/images/carenavigatorlogo.svg" alt="Avexa" width={142} height={30} />
      </div>
      <div className={styles.headerRight}>
        {showSos && (
          <button className={styles.sosBtn}>
            <SosIcon size={13} color="#fff" />
            {t.sos}
          </button>
        )}
        <button className={styles.closeBtn} onClick={onClose}>
          <CloseIcon size={18} color="#fff" />
        </button>
      </div>
    </div>
  );
}

export default function HealthChatModal({ onClose }) {
  const [screen, setScreen] = useState(SCREEN.CARENAVIGATOR);
  const [lang, setLang] = useState("en");
  const [agreed, setAgreed] = useState(false);
  const [patientPersona, setPatientPersona] = useState(null);
  const [selectedSymptomCat, setSelectedSymptomCat] = useState(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [symptomAnswers, setSymptomAnswers] = useState({});
  const [selectedCareType, setSelectedCareType] = useState(null);
  const [selectedVisitModel, setSelectedVisitModel] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [patientData, setPatientData] = useState({});
  const [apptData, setApptData] = useState({});
  const [patientState, setPatientState] = useState({
    name: '',
    mobile: '',
    dob: '',
    ageGroup: '',
    occupation: '',
    marital: '',
    heightIn: '',
    weightIn: '',
    otp: ['', '', '', '', '', ''],
    otpSent: false,
    otpVerified: false,
    nameError: '',
    mobileError: '',
  });

  const mergePatientState = (d) => setPatientState(prev => ({ ...prev, ...d }));

  const t = translations[lang];
  const showSos = SOS_SCREENS.has(screen);
  const showHeader = !NO_HEADER_SCREENS.has(screen);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const go = (s) => setScreen(s);

  function handleSymptomComplete({ selectedSymptoms: syms, answers }) {
    setSelectedSymptoms(syms);
    setSymptomAnswers(answers);
    go(SCREEN.TRIAGE);
  }

  function handleWhatNext(actionId) {
    if (actionId === "book") go(SCREEN.CONSULT_TYPE);
    else if (actionId === "programs") go(SCREEN.HEALTH_PROGRAMS);
    else if (actionId === "symptoms") {
      setSelectedSymptoms([]);
      go(SCREEN.SYMPTOM_PICKER);
    }
  }

  function handlePatientType(type) {
    if (type === "existing") go(SCREEN.EXISTING_PATIENT);
    else go(SCREEN.PATIENT_DETAILS);
  }

  function handlePatientDetails(data) {
    setPatientData(data);
    go(SCREEN.PATIENT_PERSONA);
  }

  function handleBookAppointment(data) {
    setApptData({ ...patientData, ...data });
    go(SCREEN.CONFIRM_APPOINTMENT);
  }

  const [selectedSpecialty, setSelectedSpecialty] = useState(null);

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {showHeader && (
          <Header t={t.header} onClose={onClose} showSos={showSos} />
        )}
        {/* {screen===SCREEN.CARENAVIGATOR &&(
          <ChatCareNavigatorWelcomeScreen   
          patientName={patientPersona?.name}
  // onSelect={(id) => {
  //   if (id === "symptoms") go(SCREEN.SYMPTOM_PICKER);
  //   else if (id === "book" || id === "consult") go(SCREEN.CONSULT_TYPE);
  //   else if (id === "lifeStage") go(SCREEN.LIFESTAGE);
  //   else if (id === "healthAssessment") go(SCREEN.HEALTHASSESSMENT);
  //   else if (id === "resources") go(SCREEN.HEALTH_PROGRAMS);
  //   else if (id === "ask") go(SCREEN.CHAT);
  // }}
  onSelect={()=>{
    go(SCREEN.WELCOME)
  }}
  onBack={() => go(SCREEN.PATIENT_PERSONA)}
            onClose={onClose}

    />
        )} */}
        {screen === SCREEN.CARENAVIGATOR && (
  <ChatCareNavigatorWelcomeScreen
    patientName={patientPersona?.name}
    lang={lang}                        
    onSelect={() => go(SCREEN.WELCOME)}
    onBack={() => go(SCREEN.PATIENT_PERSONA)}
    onClose={onClose}
  />
)}
        {/* 1. Welcome + Language */}
        {screen === SCREEN.WELCOME && (
          <WelcomeScreen
            onStart={(langCode) => {
              setLang(langCode);
              go(SCREEN.CONSENT);
            }}

            onClose={onClose}
          />
        )}

        {/* 2. Consent */}
        {screen === SCREEN.CONSENT && (
          <ConsentScreen
            t={t.consent}
            agreed={agreed}
            onToggle={() => setAgreed((p) => !p)}
            onExit={onClose}
            onAgree={() => go(SCREEN.PATIENT_DETAILS)}
          />
        )}

        {screen === SCREEN.PATIENT_DETAILS && (
          <PatientDetailsScreen
            patientState={patientState}
            onPatientStateChange={mergePatientState}
            lang={lang}
            onContinue={handlePatientDetails}
            onBack={() => go(SCREEN.CONSENT)}
          />
        )}

        {/* 3. Patient Persona */}
        {screen === SCREEN.PATIENT_PERSONA && (
          <PersonalizePersonaScreen
            patientState={patientState}
            onPatientStateChange={mergePatientState}
            onComplete={(data) => {
              setPatientPersona(data);
              go(SCREEN.CHAT_HOME);
            }}
            onBack={() => go(SCREEN.PATIENT_DETAILS)}
          />
        )}

        {/* 4. Chat Home */}
        {screen === SCREEN.CHAT_HOME && (
          <ChatHomeScreen
            patientName={patientPersona?.name}
            onSelect={(id) => {
              if (id === "symptoms") go(SCREEN.SYMPTOM_PICKER);
              else if (id === "book" || id === "consult")
                go(SCREEN.CONSULT_TYPE);
              else if (id === "lifeStage") go(SCREEN.LIFESTAGE);
              else if (id === "healthAssessment") go(SCREEN.HEALTHASSESSMENT);
              else if (id === "resources") go(SCREEN.HEALTH_PROGRAMS);
              else if (id === "ask") go(SCREEN.CHAT);
            }}
            onBack={() => go(SCREEN.PATIENT_PERSONA)}
          />
        )}

        {/* 5. Symptom Picker */}
        {screen === SCREEN.SYMPTOM_PICKER && (
          <SymptomPickerScreen
            lang={lang}
            onSelect={(cat) => {
              setSelectedSymptomCat(cat);
              // go(SCREEN.SYMPTOM_CHAT);
            }}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}

        {/* HealthAssessment */}
        {screen === SCREEN.HEALTHASSESSMENT && (
          <HealthAssessment
            lang={lang}
            // onSelect={(cat) => {
            //   setSelectedSymptomCat(cat);
            //   go(SCREEN.SYMPTOM_CHAT);
            // }}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}

        {/* 6. Symptom Chat Q&A */}
        {screen === SCREEN.SYMPTOM_CHAT && (
          <SymptomChatScreen
            lang={lang}
            onComplete={handleSymptomComplete}
            onBack={() => go(SCREEN.SYMPTOM_PICKER)}
          />
        )}

        {screen === SCREEN.LIFESTAGE && (
          <LifeSatgeScreen
            lang={lang}
            // onContinue={handlePatientDetails}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}

        {screen === SCREEN.CHAT && (
          <ChatScreen
            lang={lang}
            t={t.chat}
            tVoice={t.voice}
            onSummary={(data) => {
              console.log("Summary:", data);
              //go(SCREEN.SYMPTOM_CHAT);
            }}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}

        {/* 7. Triage */}
        {screen === SCREEN.TRIAGE && (
          <TriageScreen
            onComplete={() => go(SCREEN.TRIAGE_RESULTS)}
            onBack={() => go(SCREEN.SYMPTOM_CHAT)}
          />
        )}

        {/* 8. Triage Results */}
        {screen === SCREEN.TRIAGE_RESULTS && (
          <TriageResultsScreen
            symptomCat={selectedSymptomCat}
            onNext={() => go(SCREEN.RECOMMENDED)}
            onSeekHelp={() => go(SCREEN.RECOMMENDED)}
            onBack={() => go(SCREEN.SYMPTOM_CHAT)}
          />
        )}

        {/* 9. Recommended */}
        {screen === SCREEN.RECOMMENDED && (
          <RecommendedScreen
            onBook={() => go(SCREEN.CONSULT_TYPE)}
            onTips={() => go(SCREEN.HEALTH_PROGRAMS)}
            onTests={() => go(SCREEN.RATING)}
            onBack={() => go(SCREEN.TRIAGE_RESULTS)}
          />
        )}

        {/* ── 4 NEW BOOKING SCREENS ─────────────────────────────── */}

        {/* 10. Consult Type */}
        {screen === SCREEN.CONSULT_TYPE && (
          <ConsultTypeScreen
            onSelect={(careType) => {
              setSelectedCareType(careType);
              setSelectedSpecialty("specialty");
              go(SCREEN.REASONSELECTION);
            }}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}

        {screen === SCREEN.REASONSELECTION && (
          <ReasonSelectionScreen
            //careType={bookingData.type}
            onSelect={(careType) => {
              go(SCREEN.SELECT_VISIT_MODEL);
            }}
            onBack={() => go(SCREEN.CONSULT_TYPE)}
          />
        )}

        {/* 11. Select Care Type / Specialty */}
        {screen === SCREEN.SELECT_CARE_TYPE && (
          <SelectCareTypeScreen
            selectedCareType={selectedCareType}
            recommendedSpecialty={selectedCareType?.label || "Gynecologist"}
            onSelect={(sp) => {
              setSelectedCareType(sp);
              go(SCREEN.SELECT_VISIT_MODEL);
            }}
            onBack={() => go(SCREEN.CONSULT_TYPE)}
          />
        )}

        {/* 12. Select Visit Model */}
        {screen === SCREEN.SELECT_VISIT_MODEL && (
          <SelectVisitModelScreen
            onSelect={(model) => {
              setSelectedVisitModel(model);
              go(SCREEN.SELECT_DOCTOR);
            }}
            onBack={() => go(SCREEN.REASONSELECTION)}
          />
        )}

        {/* 13. Select Doctor */}
        {screen === SCREEN.SELECT_DOCTOR && (
          <SelectDoctorScreen
            onSelect={(doc) => {
              setSelectedDoctor(doc);
              setPatientData((p) => ({
                ...p,
                doctor: doc,
                careType: selectedCareType,
                visitModel: selectedVisitModel,
              }));
              go(SCREEN.BOOK_APPOINTMENT);
            }}
            onBack={() => go(SCREEN.SELECT_VISIT_MODEL)}
          />
        )}

        {/* ── EXISTING SCREENS ──────────────────────────────────── */}

        {screen === SCREEN.BOOK_APPOINTMENT && (
          <BookAppointmentScreen
            lang={lang}
            patientData={patientData}
            onBook={handleBookAppointment}
            onMaybeLater={() => go(SCREEN.RATING)}
            onBack={() => go(SCREEN.SELECT_DOCTOR)}
          />
        )}

        {screen === SCREEN.CONFIRM_APPOINTMENT && (
          <ConfirmAppointmentScreen
            lang={lang}
            apptData={apptData}
            onEdit={() => go(SCREEN.BOOK_APPOINTMENT)}
            onConfirm={() => go(SCREEN.BOOKINGPATIENTDETAILS)}
            onBack={() => go(SCREEN.BOOK_APPOINTMENT)}
          />
        )}

        {screen === SCREEN.BOOKINGPATIENTDETAILS && (
          <BookingPatientDetailsScreen
            lang={lang}
            apptData={apptData}
            onConfirm={() => go(SCREEN.PAYMENT)}
            onBack={() => go(SCREEN.CONFIRM_APPOINTMENT)}
          />
        )}

        {/* {screen === SCREEN.BOOKINGPATIENTDETAILS && (
          <BookingPatientDetailsScreen
            //careType={bookingData.type}
            onSelect={go(SCREEN.PAYMENT)}
            onBack={() => go(SCREEN.CONFIRM_APPOINTMENT)}
          />
        )} */}

        {/* PaymentScreen.jsx */}
        {screen === SCREEN.PAYMENT && (
          <PaymentScreen
            lang={lang}
            apptData={apptData}
            onSelect={() => {
              go(SCREEN.BOOKINGSUCCESSSCREEN);
            }}
            onBack={() => go(SCREEN.BOOKING_PATIENT_DETAILS)}
          />
        )}

        {/* PaymentScreen.jsx */}
        {screen === SCREEN.BOOKINGSUCCESSSCREEN && (
          <BookingSuccessScreen
            lang={lang}
            apptData={apptData}
            // onSelect={() => {
            //   go(SCREEN.RATING);
            // }}
            onCloseChat={onClose}
            onBack={() => go(SCREEN.PAYMENT)}
          />
        )}

        {screen === SCREEN.SUMMARY && (
          <SummaryScreen
            t={t.summary}
            summary={null}
            selectedSymptoms={selectedSymptoms}
            onBack={() => go(SCREEN.WHAT_NEXT)}
          />
        )}

        {screen === SCREEN.WHAT_NEXT && (
          <WhatNextScreen lang={lang} onSelect={handleWhatNext} />
        )}

        {screen === SCREEN.HEALTH_PROGRAMS && (
          <HealthProgramsScreen
            lang={lang}
            //onSelect={SCREEN.PROGRAMS_DETAILS}
            onContinue={(program) => {
              setSelectedProgram(program);
              setScreen(SCREEN.PROGRAMS_DETAILS);
            }}
            onBack={() => go(SCREEN.CHAT_HOME)}
          />
        )}
        {screen === SCREEN.PROGRAMS_DETAILS && (
          <ProgramDetailScreen
            lang={lang}
            programId={selectedProgram}
            onSelect={() => {
              go(SCREEN.CHECKOUTSCREEN);
            }}
            onBack={() => go(SCREEN.HEALTH_PROGRAMS)}
          />
        )}
        {screen === SCREEN.CHECKOUTSCREEN && (
          <CheckoutScreen
            lang={lang}
            programId={selectedProgram}
            onSelect={() => go(SCREEN.SUCCESSSCREEN)}
            onBack={() => go(SCREEN.PROGRAMS_DETAILS)}
          />
        )}

        {screen === SCREEN.SUCCESSSCREEN && (
          <SuccessScreen
            lang={lang}
            // onSelect={() => go(SCREEN.CHECKOUTSCREEN)}
            onBack={() => go(SCREEN.CHECKOUTSCREEN)}
          />
        )}

        {screen === SCREEN.PATIENT_INFO && (
          <PatientInfoScreen
            lang={lang}
            onSelect={handlePatientType}
            onBack={() => go(SCREEN.CONSENT)}
          />
        )}

        {screen === SCREEN.EXISTING_PATIENT && (
          <ExistingPatientScreen
            lang={lang}
            onContinue={(data) => {
              setPatientData(data);
              go(SCREEN.CHAT_HOME);
            }}
            onBack={() => go(SCREEN.PATIENT_INFO)}
            onSwitchNew={() => go(SCREEN.PATIENT_DETAILS)}
          />
        )}

        {screen === SCREEN.RATING && (
          <RatingScreen
            lang={lang}
            onSubmit={(feedback) => {
              console.log("Feedback:", feedback);
              onClose();
            }}
            onSkip={onClose}
          />
        )}

        {showHeader && <div className={styles.footer}>{t.footer}</div>}
      </div>
    </div>
  );
}
